/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import ComponentBase from '../../ComponentBase';
import CLog, { LogTypes } from '../../ComponentLogger';
import Observable from '../../Observable/Observable';
import { ISelectItem } from './ISelectItem';
import SelectItemData from './SelectItemData';
import SelectListItemViewModel from './SelectListItemViewModel';
import ClassUtil from '../../ClassUtil';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

@cc._decorator.ccclass
@cc._decorator.executeInEditMode
export default class SelectListViewModel<T> extends ComponentBase
{
    @property(cc.Node)
    protected indicatorNode: cc.Node = null;

    @property(cc.ScrollView)
    protected scrollView: cc.ScrollView = null;

    @property(cc.Prefab)
    protected listItemPrefab: cc.Prefab = null;

    @property(cc.EditBox)
    protected filterEditBox: cc.EditBox = null;
    private _filterObservableString: Observable<string> = new Observable<string>();

    private _selectedIndexObservable: Observable<number> = new Observable<number>(-1);
    public set SelectedIndex(value: number) { this._selectedIndexObservable.Value = value; }
    public get SelectedIndex(): number { return this._selectedIndexObservable.Value; }

    public set Selected(value: ISelectItem<T>)
    {
        let selected: SelectItemData<T> = this.items.find(item => item.ItemId === value.ItemId);
        this._selectedIndexObservable.Value = this.items.indexOf(selected);
    }
    public get Selected(): ISelectItem<T> { return this.items[this._selectedIndexObservable.Value];  }

    public OnSelectedIndexChanged(fnOnchange: () => any): void
    {
        this._selectedIndexObservable.OnChanged(fnOnchange);
    }

    @property({ type: cc.Integer, min: 0, max: 100 })
    protected set selectedIndex(value: number) { this._selectedIndexObservable.Value = value; }
    protected get selectedIndex(): number { return this._selectedIndexObservable.Value; }

    @property([SelectItemData])
    protected items: SelectItemData<T>[] = [];

    protected containerLayout: cc.Layout = null;
    private _layoutWidget: cc.Widget = null;

    private _lastItemListSize: number = 0;

    private _firstUpdate: boolean = true;

    /** @hidden */
    protected onLoad(): void
    {
        this.containerLayout = this.getComponentInChildren(cc.Layout);

        if (!this.containerLayout)
        {
            CLog.error('No layout container.', this, LogTypes.Initialization);
            return;
        }

        this._layoutWidget = this.containerLayout.getComponent(cc.Widget);
    }

    /** @hidden */
    protected start(): void
    {
        this.items.forEach(item =>
        {
            item.OnChanged(() => this.updateListUI());
        }, this);

        if (this.filterEditBox && !CC_EDITOR)
        {
            this.AddComponentEventHandler(
                ClassUtil.getName(SelectListViewModel),
                'Filter.EditingDidEnded',
                () => this._filterObservableString.Value = this.filterEditBox.string,
                this.filterEditBox.editingDidEnded
            );

            this._filterObservableString.OnChanged(this.onFilterChangedHandler.bind(this));
        }

        this._selectedIndexObservable.OnChanged(this.onSelectedIndexChangedHandler.bind(this));
        this.selectedIndex = 0;

        this.updateListUI();
    }

    /** @hidden */
    protected update(): void
    {
        if (this._firstUpdate)
        {
            this.selectedIndex = 0;
            this.onSelectedIndexChangedHandler();

            if (this.scrollView) { this.scrollView.scrollToTopLeft(); }

            this._firstUpdate = false;
        }

        if (this._lastItemListSize && this._lastItemListSize !== this.items.length)
        {
            this.updateListUI();
        }
    }

    public ForceUpdate(): void {
        this._lastItemListSize = -1;
    }

    // #region UI Helpers

    protected updateListUI(): void
    {
        if (!this.listItemPrefab) { return; }

        this.clearLayout();

        for (let item of this.items)
        {
            this.bindItem(item);

            let newItemNode: cc.Node = cc.instantiate(this.listItemPrefab);

            if (!newItemNode) { return; }

            let listItemVM: SelectListItemViewModel<T> = newItemNode.getComponent(SelectListItemViewModel);
            listItemVM.Parent = this;
            listItemVM.UpdateModel(item);

            this.containerLayout.node.addChild(newItemNode);
        }

        this._lastItemListSize = this.items.length;

        this.containerLayout.updateLayout();
        this.updateLayoutWidget();
        this.onSelectedIndexChangedHandler();
    }

    private setIndicatorNode(toChildNode: cc.Node): void
    {
        if (!this.indicatorNode)
        {
            CLog.error('No indicator node.', this, LogTypes.Utility);
            return;
        }

        this.indicatorNode.removeFromParent();

        toChildNode.addChild(this.indicatorNode);
        this.indicatorNode.position = cc.Vec2.ZERO;
    }

    private clearLayout(): void
    {
        this.indicatorNode.removeFromParent();
        this.node.addChild(this.indicatorNode);

        this.containerLayout.node.destroyAllChildren();
        this.containerLayout.node.children.length = 0;
    }

    // #endregion

    // #region Event Handlers
    protected onSelectedIndexChangedHandler = () =>
    {
        if (this.containerLayout.node.children.length < 1) { return; }
        let selectedIndex: number = this._selectedIndexObservable.Value;

        let selectedChildNode: cc.Node = this.containerLayout.node.children[selectedIndex];
        if (!selectedChildNode)
        {
            CLog.error(`Index out of range [ ${selectedIndex} ].`, this, LogTypes.Utility);
            this.selectedIndex = 0;
            selectedChildNode = this.containerLayout.node.children[0];
        }

        this.setIndicatorNode(selectedChildNode);
    }

    protected onFilterChangedHandler = () =>
    {
        let filter: string = this._filterObservableString.Value.trim().toLowerCase();

        let count: number = 0;
        this.items.forEach((item =>
            {
                let displayName: string = item.DisplayName.trim().toLowerCase();

                this.containerLayout.node.children[count].active = !filter
                    || displayName.indexOf(filter) > -1;

                count++;
            }), this);

        this.containerLayout.updateLayout();
        this.updateLayoutWidget();
    }

    private updateLayoutWidget(): void
    {
        if (!this._layoutWidget) { return; }
        this._layoutWidget.top = 0;
        this._layoutWidget.updateAlignment();
    }

    private boundItems: SelectItemData<T>[] = [];
    private bindItem(item: SelectItemData<T>): void
    {
        if (this.boundItems.indexOf(item) > -1)
        {
            return;
        }

        item.OnChanged(() => this.updateListUI());

        this.boundItems.push(item);
    }

    // #endregion

    // #region List Helpers

    public Add(item: ISelectItem<T>): void
    {
        this.items.push(new SelectItemData<T>(item, () => this.updateListUI()));
    }

    public Insert(item: SelectItemData<T>, index: number): void
    {
        let newItems: SelectItemData<T>[] = [];
        let success: boolean = false;

        for (let i: number = 0; i < this.items.length; i++)
        {
            if (i === index)
            {
                newItems.push(item);
                success = true;
                i++;
            }
            newItems.push(this.items[i]);
        }

        if (success)
        {
            this.items = newItems;
            this.updateListUI();
        }
    }

    public Remove(item: SelectItemData<T>): void
    {
        this.remove((selectItem: SelectItemData<T>) => selectItem === item);
    }

    public RemoveAt(index: number): void
    {
        this.remove((item: SelectItemData<T>, itemIndex: number) => itemIndex === index);
    }

    private remove(
        fnCompare: (
            selectItem: SelectItemData<T>,
            itemIndex: number,
            items: SelectItemData<T>[]) => boolean): void
    {
        let newItems: SelectItemData<T>[] = [];
        let index: number = 0;
        let success: boolean = false;

        for (let item of this.items)
        {
            if (fnCompare(item, index++, this.items))
            {
                success = true;
                continue;
            }
            newItems.push(item);
        }

        if (success)
        {
            this.items = newItems;
            this.updateListUI();
        }
    }

    // #endregion
}
